import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Link,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress,
  Breadcrumbs,
  CircularProgress,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Menu,
  MenuItem as MuiMenuItem,
  Tabs,
  Tab
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import {
  Upload as UploadIcon,
  Description as DescriptionIcon,
  Refresh as RefreshIcon,
  InfoOutlined as InfoIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  Home as HomeIcon,
  Work as WorkIcon,
  Replay as ReplayIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Autorenew as ReprocessIcon
} from '@mui/icons-material';
import { Code as CodeIcon } from '@mui/icons-material';
import CryptoJS from 'crypto-js';

// Import the common toast components
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { AuditLoggers } from '../../../utils/auditLogger';
import { formatDate, formatDateOnly, formatDateTime, initializeFormats, getFormatConfig } from '../../../utils/global'
import { useUserRights } from '../../../context/UserRightsContext';
import { hasWorkflowProductPermission } from './workflowProductScope';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateUploadOutlinedSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateGradientFormFieldsSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateFormFieldsSx,
} from '../../../theme/harmonateStyles';
import { isHarmonateUser as isServerHarmonateUser } from '../../../auth/serverIdentity';

const harmonateBreadcrumbSx = {
  fontSize: '0.9rem',
  '& .MuiBreadcrumbs-li': { color: ht.white },
  '& a': { color: ht.white },
  '& .MuiBreadcrumbs-separator': { color: ht.white },
};

const WorkflowDrawer = ({
  selectedDocumentType,
  selectedWorkflowId,
  onFileClick,
  selectedQuarter,
  onBackToWorkflows,
  productScope,
}) => {
  // Side drawer API state
  const [drawerFiles, setDrawerFiles] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState(null);

  // Audit logs state
  const [auditLogs, setAuditLogs] = useState([]);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);

  // Redrive functionality state
  const [redriveLoading, setRedriveLoading] = useState(false);
  const [redriveConfirmOpen, setRedriveConfirmOpen] = useState(false);
  const [redriveStatusDialog, setRedriveStatusDialog] = useState({ open: false, executionArn: null, status: null });

  // Delete functionality state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [reprocessConfirmOpen, setReprocessConfirmOpen] = useState(false);
  const [fileToReprocess, setFileToReprocess] = useState(null);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [dateTimeFormat, setDateTimeFormat] = useState('DD/MM/YYYY hh:mm:ss a');

  // State for extraction details dialog
  const [extractionDetails, setExtractionDetails] = useState(null);
  const [extractionLoading, setExtractionLoading] = useState(false);
  const [extractionDialogOpen, setExtractionDialogOpen] = useState(false);
  const [currentFileForDetails, setCurrentFileForDetails] = useState(null);
  const [activeExtractionTab, setActiveExtractionTab] = useState(0);
  const [baseInternalCasData, setBaseInternalCasData] = useState(null);
  const [baseInternalCasLoading, setBaseInternalCasLoading] = useState(false);
  const [baseInternalCasError, setBaseInternalCasError] = useState(null);
  const [landingInternalCasData, setLandingInternalCasData] = useState(null);
  const [landingInternalCasLoading, setLandingInternalCasLoading] = useState(false);
  const [landingInternalCasError, setLandingInternalCasError] = useState(null);

  // State for JSON dialog
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [jsonData, setJsonData] = useState(null);
  const [jsonLoading, setJsonLoading] = useState(false);
  const [jsonError, setJsonError] = useState(null);

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [rowsToDelete, setRowsToDelete] = useState([]);
  const [fileInstances, setFileInstances] = useState([]);

  // Use the common toast hook and user rights
  const { toastState, showSuccess, showError, showInfo, hideToast } = useToast();
  const { hasPermission, isHarmonateUser } = useUserRights();

  // Initialize formats when component mounts
  useEffect(() => {
    const tenantId = localStorage.getItem('tenant_id');
    if (tenantId) {
      initializeFormats(tenantId).then(() => {
        const config = getFormatConfig();
        if (config.DateTimeFormat) {
          setDateTimeFormat(config.DateTimeFormat);
        }
      });
    }
  }, []);

  // Fetch file instances for selected workflow
  const fetchFileInstances = async (tenantWorkflowId, quarter = selectedQuarter) => {
    try {
      setDrawerLoading(true);
      setDrawerError(null);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) {
        throw new Error('No authentication token found');
      }

      if (!tenantId) {
        throw new Error('No tenant ID found');
      }

      console.log(`Fetching file instances for tenant workflow ID: ${tenantWorkflowId}`);
      console.log(`Using tenant ID: ${tenantId}`);
      console.log(`Using backend URL: ${process.env.REACT_APP_BACKEND_URL}`);

      // Add from/to params for quarter filtering
      let url = `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/by-workflow/${tenantWorkflowId}`;
      const params = new URLSearchParams();

      // Add tenantId as query parameter
      params.append('tenantId', tenantId);

      // Add from/to params for quarter filtering
      if (quarter && quarter.from && quarter.to) {
        params.append('from', quarter.from);
        params.append('to', quarter.to);
      }

      // Append query parameters to URL
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      console.log(`Making request to: ${url}`);

      // Create headers without tenantId (now passed as query parameter)
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      console.log(`Headers being sent:`, headers);

      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
      });

      console.log(`Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error text:', errorText);
        throw new Error(`Failed to fetch file instances: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      console.log('API Response for tenant workflow:', data);

      // Transform API data - now include the ID for the new API call
      const transformedFiles = data.map((file) => ({
        id: file.id, // This will be used as fileInstanceId for the PDF API (presigned-url)
        name: file.name,
        fund: file.fundName || '', // Map fundName to ID Term
        username: file.username || '', // Add username field
        processStep: file.processStep,
        createdAt: file.createdAt,
        documentDate: file.documentDate || '', // Add documentDate field
        fileInstanceId: file.id, // Use file.id for presigned-url
        fastApiFileInstanceId: file.integrationId, // Use integrationId for FastAPI
        unitOfWorkId: file.unitOfWorkId,
        workflowDefinitionSnowflakeId: file.workflowDefinitionSnowflakeId,
        processId: file.processId, // Add processId for workflow_instance_id
        approvedStatus: file.approvedStatus, // <-- Pass approvedStatus
        identifier: file.identifier || 0, // Add identifier field for error detection
        reprocessDocumentId:
          file.document?.id ?? file.documentId ?? file.document_id ?? file.id
      }));

      // Sort by createdAt descending
      transformedFiles.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      console.log('Transformed Files for workflow:', transformedFiles);
      setDrawerFiles(transformedFiles);

      if (transformedFiles.length === 0) {
        showInfo('No file instances found for this workflow');
      } else {
        showSuccess(`Successfully loaded ${transformedFiles.length} file instances`);
      }
    } catch (err) {
      console.error('Error fetching workflow file instances:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      setDrawerError(err.message);
      setDrawerFiles([]);
      showError(`Failed to load workflow files: ${err.message}`, 'Loading Error');
    } finally {
      setDrawerLoading(false);
    }
  };

  // Add these utility functions at the top of the file with other utility functions
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

// Utility function to ensure consistent key generation
const generateKey = () => {
  const keyBytes = CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY);
  const hash = CryptoJS.SHA256(keyBytes);
  return CryptoJS.lib.WordArray.create(hash.words.slice(0, 8));
};

// Encrypt data using the same method as login
const encryptData = (data) => {
  if (!data) return '';
  try {
    const stringData = data.toString();
    const key = generateKey();
    const encrypted = CryptoJS.AES.encrypt(stringData, key, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });
    return encrypted.toString();
  } catch (error) {
    console.error('Encryption failed:', error);
    throw new Error('Encryption failed: ' + error.message);
  }
};

// Then update the verifyPassword function
const verifyPassword = async () => {
  if (!password.trim()) {
    setPasswordError('Please enter your password');
    return false;
  }

  try {
    setIsVerifying(true);
    setPasswordError('');
    
    const username = localStorage.getItem('username');
    if (!username) {
      throw new Error('User session expired. Please log in again.');
    }

    // Encrypt the password using the same method as login
    const encryptedPassword = encryptData(password);

    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}auth/signin`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          username,
          password: encryptedPassword,
          applicationId: 1,
          application: "Conductor"// Using the same computer name as in your example
        })
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Incorrect password');
    }

    return true;
  } catch (error) {
    console.error('Password verification failed:', error);
    setPasswordError(error.message || 'Failed to verify password');
    return false;
  } finally {
    setIsVerifying(false);
  }
};


  // Fetch file instances when workflow or quarter changes
  useEffect(() => {
    if (selectedWorkflowId && selectedQuarter) {
      fetchFileInstances(selectedWorkflowId, selectedQuarter);
    }
  }, [selectedWorkflowId, selectedQuarter]);

  const [documentDetails, setDocumentDetails] = useState(null);
  const [currentFileProcessStep, setCurrentFileProcessStep] = useState(null);

  const handleInfoClick = async (event, fileOrRow) => {
    event.stopPropagation();
    try {
      setLogsLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        throw new Error('Authentication required');
      }

      // Handle both row and file parameter types
      const file = fileOrRow.id ? fileOrRow : fileOrRow;

      // Set the current file for details (used by Snowflake Info button)
      setCurrentFileForDetails(file);
      setSelectedFile(file);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${file.id}/with-audit-logs?tenantId=${tenantId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch audit logs: ${response.status}`);
      }

      const data = await response.json();
      setDocumentDetails(data.document);
      setAuditLogs(data.auditLogs || []);
      setCurrentFileProcessStep(file.processStep);
      console.log('Process step for selected file:', file.processStep);
      setIsLogsModalOpen(true);
    } catch (error) {
      console.error('Error in handleInfoClick:', error);
      showError('Failed to load document details and audit logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleFileClick = (params) => {
    // Don't trigger file click when clicking on checkboxes
    if (params.field === '__check__') return;
    
    // Pass both fileInstanceId (for presigned-url) and fastApiFileInstanceId (for FastAPI) to the parent
    onFileClick({
      fileInstanceId: params.row.fileInstanceId,
      fastApiFileInstanceId: params.row.fastApiFileInstanceId,
      fileName: params.row.name,
      id: params.row.id,
      unitOfWorkId: params.row.unitOfWorkId,
      workflowDefinitionSnowflakeId: params.row.workflowDefinitionSnowflakeId
    });
  };

  // Update the handleDeleteButtonClick function
const handleDeleteButtonClick = (e) => {
  e?.preventDefault();

  // Enter delete mode when nothing selected
  if (selectedRows.length === 0) {
    if (!isDeleteMode) {
      setIsDeleteMode(true);
    }
    return;
  }

  // Store the currently selected rows and show delete confirmation
  setRowsToDelete([...selectedRows]);
  setDeleteAllConfirmOpen(true);
};

const handleCancelDeleteMode = () => {
  setIsDeleteMode(false);
  setSelectedRows([]);
  setRowsToDelete([]);
  setDeleteAllConfirmOpen(false);
};
  // Handle row selection for bulk delete
  const handleRowSelection = (newSelection) => {
    setSelectedRows(newSelection);
  };

  // Handle delete button click
const handlePasswordConfirm = async () => {
  const isValid = await verifyPassword();
  if (isValid) {
    setPasswordDialogOpen(false);
    setPassword('');
    
    try {
      setIsBulkDeleting(true);
      const token = localStorage.getItem('token');
      // Get file names for logging
      const fileNames = rowsToDelete.map(id => {
        const file = fileInstances.find(f => f.id === id);
        return file?.name || file?.fileName ;
      });
      // Log the bulk delete attempt
      await AuditLoggers.bulkFileDeletionInitiated(
        rowsToDelete,
        fileNames,
        'workflow',
        { 
          workflowId: selectedWorkflowId,
          quarter: selectedQuarter
        }
      );
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/bulk-delete`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(rowsToDelete)
        }
      );
      if (!response.ok) {
        throw new Error('Failed to delete files');
      }
      const result = await response.json();
      
      // FastAPI bulk status update must succeed before showing success
      const statusUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/conductor-v2-status/bulk-save`;
      console.log('Calling bulk status update API at URL:', statusUrl);
      
      // Prepare records for bulk status update
      const statusRecords = rowsToDelete.map(id => {
        const file = fileInstances.find(f => f.id === id);
        return {
          file_instance_id: file?.fastApiFileInstanceId?.toString() || id?.toString(),
          workflow_instance_id: file?.processId?.toString() || id?.toString(),
          status: 'DELETED'
        };
      });
      
      const statusResponse = await fetch(statusUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          records: statusRecords
        })
      });

      if (!statusResponse.ok) {
        const statusErrorText = await statusResponse.text();
        console.error('Bulk status update error response text:', statusErrorText);
        throw new Error(`Files deleted in Conductor, but FastAPI bulk status update failed: ${statusResponse.status} - ${statusErrorText}`);
      }

      const statusResult = await statusResponse.json();
      console.log('Bulk status update successful:', statusResult);
      
      showSuccess(`Successfully deleted ${result.deletedCount} files`);
      setSelectedRows([]);
      setRowsToDelete([]);
      setIsDeleteMode(false);
      handleRetry(); // Refresh the list
    } catch (error) {
      console.error('Error deleting files:', error);
      showError(error.message || 'Failed to delete files');
    } finally {
      setIsBulkDeleting(false);
    }
  }
};

  // Handle bulk delete operation
 const handleBulkDelete = () => {
  if (rowsToDelete.length === 0) {
    showError('No files selected for deletion');
    setDeleteAllConfirmOpen(false);
    return;
  }

  // Close confirmation and prompt for password before deleting
  setDeleteAllConfirmOpen(false);
  setPassword('');
  setPasswordError('');
  setPasswordDialogOpen(true);
};

// Add this with your other useEffect hooks
useEffect(() => {
  return () => {
    setIsDeleteMode(false);
    setSelectedRows([]);
  };
}, [selectedWorkflowId]);

  // Delete functionality
  const handleDeleteClick = (event, row) => {
    event.stopPropagation();
    setFileToDelete(row);
    setDeleteConfirmOpen(true);
  };

  const handleReprocessClick = (event, row) => {
    event.stopPropagation();
    setFileToReprocess(row);
    setReprocessConfirmOpen(true);
  };

  const handleReprocessConfirm = async () => {
    const documentId = fileToReprocess?.reprocessDocumentId;
    if (!documentId) {
      showError('Document ID not found for reprocess');
      return;
    }

    const baseUrl = process.env.REACT_APP_API_URL || '';
    const reprocessUrl = `${baseUrl}/documents/${documentId}/reprocess`;

    try {
      setIsReprocessing(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(reprocessUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Reprocess failed: ${response.status}`);
      }

      showSuccess('Reprocess started successfully');
      setReprocessConfirmOpen(false);
      setFileToReprocess(null);

      if (selectedWorkflowId && selectedQuarter) {
        fetchFileInstances(selectedWorkflowId, selectedQuarter);
      }
    } catch (error) {
      console.error('Error reprocessing document:', error);
      showError(error.message || 'Failed to reprocess file');
    } finally {
      setIsReprocessing(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete?.id) {
      showError('File ID not found');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Log file deletion attempt
      try {
        const fileName = fileToDelete.name || fileToDelete.fileName || fileToDelete.originalFileName || 'unknown';
        console.log('Logging file deletion attempt...', { fileToDelete, fileName });
        await AuditLoggers.fileDeletionInitiated(
          fileToDelete.id,
          fileName,
          fileToDelete.workflowName || 'unclassified'
        );
      } catch (logError) {
        console.error('Error logging file deletion:', logError);
        // Continue with deletion even if logging fails
      }

      const deleteUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${fileToDelete.id}/delete`;
      console.log('Calling delete API at URL:', deleteUrl);

      const response = await fetch(deleteUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Delete response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Delete error response text:', errorText);
        throw new Error(`Failed to delete file: ${response.status} - ${errorText}`);
      }

      // FastAPI status update must succeed before showing success
      const statusUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/conductor-v2-status/save`;
      console.log('Calling status update API at URL:', statusUrl);
      
      const statusResponse = await fetch(statusUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file_instance_id: fileToDelete.fastApiFileInstanceId?.toString() || fileToDelete.id?.toString(),
          workflow_instance_id: fileToDelete.processId?.toString() || fileToDelete.id?.toString(),
          status: 'DELETED'
        })
      });

      if (!statusResponse.ok) {
        const statusErrorText = await statusResponse.text();
        console.error('Status update error response text:', statusErrorText);
        throw new Error(`File deleted in Conductor, but FastAPI status update failed: ${statusResponse.status} - ${statusErrorText}`);
      }

      const statusResult = await statusResponse.json();
      console.log('Status update successful:', statusResult);

      showSuccess('File deleted successfully');
      setDeleteConfirmOpen(false);
      setFileToDelete(null);

      // Refresh file instances
      if (selectedWorkflowId && selectedQuarter) {
        fetchFileInstances(selectedWorkflowId, selectedQuarter);
      }
    } catch (error) {
      console.error('Error during file deletion:', error);
      showError(`Failed to delete file: ${error.message}`);
    }
  };

  // Redrive functionality
  const handleRedriveClick = () => {
    setRedriveConfirmOpen(true);
  };

  const handleRedriveConfirm = async () => {
    if (!documentDetails?.unitOfWorkId) {
      showError('Unit of Work ID not found');
      return;
    }

    try {
      setRedriveLoading(true);
      setRedriveConfirmOpen(false);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('Starting redrive process for Unit of Work:', documentDetails.unitOfWorkId);

      // Step 1: Get workflow execution using unitOfWorkId
      const workflowExecutionUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-executions/uow/${documentDetails.unitOfWorkId}`;
      console.log('Step 1: Fetching workflow execution from URL:', workflowExecutionUrl);

      const workflowExecutionResponse = await fetch(workflowExecutionUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Step 1: Workflow execution response status:', workflowExecutionResponse.status);

      if (!workflowExecutionResponse.ok) {
        const errorText = await workflowExecutionResponse.text();
        console.error('Step 1: Error response text:', errorText);
        throw new Error(`Failed to get workflow execution: ${workflowExecutionResponse.status} - ${errorText}`);
      }

      const workflowData = await workflowExecutionResponse.json();
      console.log('Step 1: Workflow execution data received:', workflowData);
      const workflowExecutionId = workflowData.id;

      if (!workflowExecutionId) {
        console.error('Step 1: No workflow execution ID found in response:', workflowData);
        throw new Error('Workflow execution ID not found in response');
      }

      console.log('Step 1: Successfully found workflow execution ID:', workflowExecutionId);

      // Step 2: Call redrive API
      const redriveUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-executions/${workflowExecutionId}/redrive`;
      console.log('Step 2: Calling redrive API at URL:', redriveUrl);
      // QA uses dev step function name, others use their environment name
      const stepFunctionName = process.env.REACT_APP_ENV === 'qa' ? 'Conductor-v2-dev-workflow' : `Conductor-v2-${process.env.REACT_APP_ENV}-workflow`;
      const stateMachineArn = `arn:aws:states:eu-west-1:${process.env.REACT_APP_AWS_ACCOUNT_ID}:stateMachine:${stepFunctionName}`;
      console.log('Step 2: Request payload:', {
        stateMachineArn: stateMachineArn
      });

      const redriveResponse = await fetch(redriveUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          stateMachineArn: stateMachineArn
        })
      });

      console.log('Step 2: Redrive response status:', redriveResponse.status);

      if (!redriveResponse.ok) {
        const errorText = await redriveResponse.text();
        console.error('Step 2: Error response text:', errorText);
        throw new Error(`Failed to redrive workflow: ${redriveResponse.status} - ${errorText}`);
      }

      const redriveData = await redriveResponse.json();
      console.log('Step 2: Redrive response data received:', redriveData);
      const newExecutionArn = redriveData.newExecutionArn;

      if (!newExecutionArn) {
        console.error('Step 2: No execution ARN found in response:', redriveData);
        throw new Error('New execution ARN not found in redrive response');
      }

      console.log('Step 2: Successfully received new execution ARN:', newExecutionArn);

      showSuccess('Workflow redrive initiated successfully');

      // Open status dialog
      setRedriveStatusDialog({
        open: true,
        executionArn: newExecutionArn,
        status: 'RUNNING'
      });

      // Start polling for status
      pollRedriveStatus(newExecutionArn);

    } catch (error) {
      console.error('Error during redrive:', error);
      showError(`Failed to redrive workflow: ${error.message}`);
    } finally {
      setRedriveLoading(false);
    }
  };

  const pollRedriveStatus = async (executionArn) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const statusResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-executions/${encodeURIComponent(executionArn)}/status`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        setRedriveStatusDialog(prev => ({
          ...prev,
          status: statusData.status
        }));

        // Continue polling if still running
        if (statusData.status === 'RUNNING') {
          setTimeout(() => pollRedriveStatus(executionArn), 3000); // Poll every 3 seconds
        } else {
          // Refresh audit logs when completed
          if (statusData.status === 'SUCCEEDED') {
            showSuccess('Workflow redrive completed successfully');
            // Refresh the audit logs
            setTimeout(() => {
              const currentRow = { id: documentDetails.id };
              handleInfoClick(new Event('click'), currentRow);
            }, 2000);
          } else if (statusData.status === 'FAILED') {
            showError('Workflow redrive failed');
          }
        }
      }
    } catch (error) {
      console.error('Error polling redrive status:', error);
    }
  };

  const handleCloseStatusDialog = () => {
    setRedriveStatusDialog({ open: false, executionArn: null, status: null });
  };

  const handleRetry = () => {
    if (selectedWorkflowId && selectedQuarter) {
      fetchFileInstances(selectedWorkflowId, selectedQuarter);
    }
  };

  // Permission flags
  const fallbackHarmonate = isServerHarmonateUser();
  const effectiveIsHarmonate = typeof isHarmonateUser === 'boolean' ? isHarmonateUser : fallbackHarmonate;
  const canDeleteWorkflow = hasWorkflowProductPermission(
    hasPermission,
    effectiveIsHarmonate,
    productScope,
    'delete'
  );

  // State for search
  const [searchQuery, setSearchQuery] = useState('');

  // State for menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event, file) => {
    event.stopPropagation();
    setSelectedFile(file);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFile(null);
    setCurrentFileForDetails(null);
  };

  const handleViewRawJson = async (file) => {
    try {
      setJsonLoading(true);
      setJsonError(null);
      setJsonDialogOpen(true);

      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!file.unitOfWorkId) {
        throw new Error('File does not have a unitOfWorkId');
      }

      // Construct the S3 path for the extracted JSON file using the pattern FI-{fastApiFileInstanceId}-WI-{processId}.json
      const s3Path = `t-${tenantId}/uow-${file.unitOfWorkId}/extracted/output/FI-${file.fastApiFileInstanceId}-WI-${file.processId}.json`;

      // Log the file object for debugging
      console.log('File object:', file);

      // Get presigned URL using the new endpoint
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/presigned-url-for-path` +
        `?s3Path=${encodeURIComponent(s3Path)}` +
        `&tenantId=${encodeURIComponent(tenantId)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
          `Failed to get presigned URL: ${response.status} ${response.statusText}`
        );
      }

      const { url } = await response.json();

      try {
        // Try to fetch and display the JSON content
        const jsonResponse = await fetch(url, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        if (!jsonResponse.ok) {
          throw new Error('Failed to fetch extracted JSON data');
        }

        const data = await jsonResponse.json();
        setJsonData({
          content: JSON.stringify(data, null, 2),
          downloadUrl: url,
          isBinary: false,
          fileName: `FI-${file.id}.json`
        });
      } catch (jsonError) {
        console.error('Error fetching extracted JSON:', jsonError);
        throw new Error('Could not load extracted JSON data. It might not be available yet.');
      }
    } catch (error) {
      console.error('Error:', error);
      setJsonError(error.message);
      showError(`Failed to load file: ${error.message}`);
    } finally {
      setJsonLoading(false);
    }
  };

  const handleMoveToUnidentified = async () => {
    if (!selectedFile) return;

    try {
      // Log the move to unidentified action
      console.log('Attempting to log move to unidentified action...', { 
        fileInstanceId: selectedFile.id, 
        fileName: selectedFile.file_name 
      });
      
      try {
        console.log('Calling AuditLoggers.documentMovedToUnidentified...');
         const result = await AuditLoggers.documentMovedToUnidentified(
        selectedFile.id, 
        selectedFile.file_name || selectedFile.name || 'Unknown File',
        {
          fileName: selectedFile.file_name || selectedFile.name || 'Unknown File'
        }
      );
      console.log('Audit log result:', result);
    } catch (logError) {
        console.error('Error logging move to unidentified action:', logError);
        // Don't fail the operation if logging fails
      }

      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(
        `${baseUrl}conductor/file-instances/${selectedFile.id}/clear-workflow-definition`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        showSuccess('File moved to unidentified successfully');
        if (selectedWorkflowId && selectedQuarter) {
          fetchFileInstances(selectedWorkflowId, selectedQuarter);
        }
      } else {
        throw new Error('Failed to move file');
      }
    } catch (error) {
      console.error('Error moving file to unidentified:', error);
      showError('Failed to move file to unidentified');
    } finally {
      handleMenuClose();
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveExtractionTab(newValue);

    // Fetch data for the selected tab if not already loaded
    if (newValue === 1 && !baseInternalCasData && !baseInternalCasLoading) {
      fetchBaseInternalCas();
    } else if (newValue === 2 && !landingInternalCasData && !landingInternalCasLoading) {
      fetchLandingInternalCas();
    }
  };

  const fetchWorkflowDefinition = async (workflowDefinitionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-definition-snowflake/${workflowDefinitionId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to fetch workflow definition:', response.status, errorText);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching workflow definition:', error);
      return null;
    }
  };

 const fetchBaseInternalCas = async () => {
  if (!currentFileForDetails) return;
  
  setBaseInternalCasLoading(true);
  setBaseInternalCasError(null);
  
  try {
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenant_integration');
    const currentFile = drawerFiles.find(file => file.id === currentFileForDetails.id);
    
    if (!currentFile) throw new Error('Current file not found');
    
    // First, get the workflow definition to get the Snowflake table name
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-definition-snowflake/${currentFile.workflowDefinitionSnowflakeId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch workflow definition: ${response.status} ${errorText}`);
    }

    const workflowDef = await response.json();
    console.log('Workflow definition response:', workflowDef); // Add this for debugging
    
    if (!workflowDef) {
      throw new Error('Workflow definition not found');
    }
    
    const tableName = workflowDef.snowflakeTableName; // Access directly from the object
    if (!tableName) {
      throw new Error('Snowflake table name not found in workflow definition');
    }

    console.log('Using table name:', tableName);
    
    // Now make the actual API call with the table name
    const baseResponse = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/base?file_id=${currentFile.fastApiFileInstanceId}&workflow_id=${currentFile.processId}&tenant_id=${tenantId}&table_name=${tableName}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    
    if (!baseResponse.ok) {
      const errorText = await baseResponse.text();
      throw new Error(`HTTP error! status: ${baseResponse.status}, details: ${errorText}`);
    }
    
    const data = await baseResponse.json();
    setBaseInternalCasData(data);
  } catch (error) {
    console.error('Error in fetchBaseInternalCas:', error);
    setBaseInternalCasError(error.message);
    showError(error.message); // Show error to user
  } finally {
    setBaseInternalCasLoading(false);
  }
};

  const fetchLandingInternalCas = async () => {
  if (!currentFileForDetails) return;

  setLandingInternalCasLoading(true);
  setLandingInternalCasError(null);

  try {
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenant_integration');
    const currentFile = drawerFiles.find(file => file.id === currentFileForDetails.id);

    if (!currentFile) throw new Error('Current file not found');

    // First, get the workflow definition to get the Snowflake table name
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-definition-snowflake/${currentFile.workflowDefinitionSnowflakeId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch workflow definition: ${response.status} ${errorText}`);
    }

    const workflowDef = await response.json();
    console.log('Workflow definition response for landing:', workflowDef);
    
    if (!workflowDef) {
      throw new Error('Workflow definition not found');
    }
    
    const tableName = workflowDef.snowflakeTableName;
    if (!tableName) {
      throw new Error('Snowflake table name not found in workflow definition');
    }

    console.log('Using landing table name:', tableName);

    // Make the landing API call with the table name
    const landingResponse = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/landing?file_id=${currentFile.fastApiFileInstanceId}&workflow_id=${currentFile.processId}&tenant_id=${tenantId}&table_name=${tableName}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!landingResponse.ok) {
      const errorText = await landingResponse.text();
      throw new Error(`HTTP error! status: ${landingResponse.status}, details: ${errorText}`);
    }

    const data = await landingResponse.json();
    setLandingInternalCasData(data);
  } catch (error) {
    console.error('Error in fetchLandingInternalCas:', error);
    setLandingInternalCasError(error.message);
    showError(error.message);
  } finally {
    setLandingInternalCasLoading(false);
  }
};

  const handleSnowflakeInfoClick = async () => {
    try {
      setExtractionLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_integration');

      // Reset states when opening dialog
      setActiveExtractionTab(0);
      setBaseInternalCasData(null);
      setBaseInternalCasError(null);
      setLandingInternalCasData(null);
      setLandingInternalCasError(null);

      // Get the current file's details
      if (!currentFileForDetails) {
        console.error('No file selected for details');
        showError('Please select a file first');
        return;
      }

      const currentFile = drawerFiles.find(file => file.id === currentFileForDetails.id);
      if (!currentFile) {
        console.error('Current file not found in drawerFiles:', { drawerFiles, currentFileForDetails });
        showError('Could not find current file details');
        return;
      }

      // Use fastApiFileInstanceId for file_instance_id and processId for workflow_instance_id
      const fileInstanceId = currentFile.fastApiFileInstanceId;
      const workflowInstanceId = currentFile.processId;

      if (!fileInstanceId) {
        console.error('File instance ID is missing in file data:', currentFile);
        showError('Could not find file instance ID');
        return;
      }

      console.log('Fetching extraction details for file:', fileInstanceId, 'workflow:', workflowInstanceId);
      let apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/conductor-extraction-json?file_instance_id=${fileInstanceId}&workflow_instance_id=${workflowInstanceId}`;

      // Add tenant_id parameter if available
      if (tenantId) {
        apiUrl += `&tenant_id=${tenantId}`;
      }

      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setExtractionDetails(data);
      setExtractionDialogOpen(true);
    } catch (error) {
      console.error('Error fetching extraction details:', error);
      showError('Failed to load extraction details');
    } finally {
      setExtractionLoading(false);
    }
  };

  const columns = [
    { field: 'name', headerName: 'File Name', flex: 1, resizable: true },
    {
      field: 'createdAt',
      headerName: 'Created Date',
      flex: 1,
      width: 150,
      resizable: true,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '0.7rem' }}>
          {params.value ? formatDateTime(params.value, dateTimeFormat) : ''}
        </Typography>
      ),
    },
    { field: 'fund', headerName: 'Fund', width: 150, resizable: true },
    { field: 'documentDate', 
      headerName: 'Document Date', 
      width: 200, 
      resizable: true,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '0.7rem' }}>
          {params.value ? formatDateOnly(params.value, dateTimeFormat?.split(' ')[0]) : ''}
        </Typography>
      ),
    },
    { field: 'username', headerName: 'Uploaded By', width: 150, resizable: true },
    {
      field: 'processStep',
      headerName: 'Process Step',
      width: 130,
      resizable: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
          <span>{params.value}</span>
          {params.row.processStep === 'Failed' && (
            <IconButton
              size="small"
              onClick={(e) => handleMenuClick(e, params.row)}
              sx={{ ml: 1 }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: '',
      width: 140,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => '',
      renderCell: (params) => (
        <>
          {isHarmonateUser && (
            <Tooltip title="View details">
              <IconButton
                size="small"
                onClick={(e) => handleInfoClick(e, params.row)}
                sx={{ color: 'primary.main' }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Delete file">
            <IconButton
              size="small"
              onClick={(e) => handleDeleteClick(e, params.row)}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reprocess file">
            <IconButton
              size="small"
              onClick={(e) => handleReprocessClick(e, params.row)}
              sx={{ color: 'action.active' }}
            >
              <ReprocessIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.identifier === 1 && (
            <Tooltip title="Error detected">
              <IconButton
                size="small"
                sx={{ color: 'error.main' }}
              >
                <WarningIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  // Filter files based on search query
  const filteredFiles = drawerFiles.filter((file) =>
    file.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    file.fund.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <Box sx={{ ...harmonatePageSx, minHeight: '100vh' }}>
        {/* Breadcrumbs and Search Bar Row */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Breadcrumbs */}
          <Breadcrumbs aria-label="breadcrumb" sx={harmonateBreadcrumbSx}>
            <Link
              underline="hover"
              color="inherit"
              onClick={onBackToWorkflows}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <HomeIcon sx={{ fontSize: '1rem' }} />
              Workflows
            </Link>
            <Typography sx={{ color: ht.white, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WorkIcon sx={{ fontSize: '1rem' }} />
              {selectedDocumentType}
            </Typography>
          </Breadcrumbs>

          <Box
            component="form"
            autoComplete="off"
            noValidate
            onSubmit={(e) => e.preventDefault()}
            sx={{ display: 'flex', gap: 2, alignItems: 'center' }}
          >
    {/* Search Bar */}
   <TextField
      label="Search Files or Funds"
      variant="outlined"
      size="medium"
      autoComplete="off"
      type="search"
      key={selectedWorkflowId || 'search-bar'}
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      sx={{
        width: 350,
        ...harmonateGradientFormFieldsSx,
        '& .MuiInputBase-root': {
          fontSize: '0.9rem',
          height: 48,
        },
        '& .MuiInputLabel-root': {
          fontSize: '0.9rem',
          color: ht.white,
          '&.Mui-focused': { color: ht.white },
          '&.MuiInputLabel-shrink': { color: ht.white },
        },
      }}
      placeholder="Enter file name or fund name"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ color: ht.white }} />
          </InputAdornment>
        ),
      }}
      inputProps={{
        autoComplete: "new-password",
        'data-form-type': "other",
        'data-lpignore': "true",
        autoCorrect: "off",
        autoCapitalize: "none",
        spellCheck: "false",
        name: "search-files-funds",
        autoSave: "off",
        inputMode: "search"
      }}
/>
    {/* Delete controls (only for users with Workflow delete permission or Harmonate users) */}
    {canDeleteWorkflow && (
      <>
        <Button
          variant={isDeleteMode ? "outlined" : "contained"}
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDeleteButtonClick}
          disabled={!canDeleteWorkflow}
          sx={{ height: 48, minWidth: 180 }}
          type="button"
        >
          {!isDeleteMode
            ? 'Bulk Delete'
            : selectedRows.length > 0
              ? `Delete Selected (${selectedRows.length})`
              : 'Select files'}
        </Button>
        {isDeleteMode && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCancelDeleteMode}
            sx={{ height: 48, minWidth: 150 }}
            type="button"
          >
            Cancel
          </Button>
        )}
      </>
    )}
  </Box>
</Box>

        {/* Content */}
        <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, flex: 1, overflow: 'hidden' }}>
          {/* Loading state */}
          {drawerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
              <CircularProgress sx={{ color: ht.green }} />
              <Typography variant="body1" sx={{ ml: 2 }}>Loading workflow files...</Typography>
            </Box>
          ) : drawerError ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '400px', justifyContent: 'center' }}>
              <Alert severity="error" sx={{ mb: 2, maxWidth: '500px' }}>
                <Typography>Failed to load workflow files: {drawerError}</Typography>
              </Alert>
              <Button
                variant="outlined"
                onClick={handleRetry}
                sx={harmonateUploadOutlinedSx}
              >
                Retry
              </Button>
            </Box>
          ) : (
            <DataGridPro
              rows={filteredFiles}
              columns={columns}
              rowHeight={40}
              onRowClick={handleFileClick}
              pagination
              rowsPerPageOptions={[10, 25, 50]}
              pageSize={10}
              autoPageSize={false}
               checkboxSelection={isDeleteMode}
                onRowSelectionModelChange={handleRowSelection}
                rowSelectionModel={isDeleteMode ? selectedRows : []}
                disableRowSelectionOnClick
              sx={{
                ...harmonateDataGridSx,
                height: 750,
                '& .MuiDataGrid-cell': {
                  fontSize: '0.7rem',
                },
                '& .MuiDataGrid-columnHeader': {
                  fontSize: '0.9rem',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-row:hover': {
                  cursor: 'pointer'
                }
              }}
              initialState={{
                filter: {
                  filterModel: {
                    items: [
                      { columnField: 'name', operatorValue: 'contains', value: '' },
                      { columnField: 'date', operatorValue: 'equals', value: null },
                    ],
                  },
                },
              }}
              getRowId={(row) => row.id}
              noRowsOverlay={() => (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    {searchQuery ? 'No files match your search' : 'No file instances found for this workflow'}
                  </Typography>
                </Box>
              )}
            />
          )}
        </Paper>
      </Box>

      {/* Document Details & Audit Logs Modal */}
      {isLogsModalOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1300,
          }}
          onClick={() => {
            setIsLogsModalOpen(false);
            setCurrentFileProcessStep(null);
          }}
        >
          <Paper
            onClick={(e) => e.stopPropagation()}
            elevation={0}
            sx={{
              width: '95%',
              maxWidth: '1200px',
              height: '90vh',
              display: 'flex',
              flexDirection: 'column',
              p: 0,
              overflow: 'hidden',
              ...harmonateCardSx,
              bgcolor: ht.white,
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${ht.line}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: ht.green,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, flexGrow: 1, color: ht.white, fontSize: '1.1rem' }}
                >
                  Document Details & Audit Logs
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSnowflakeInfoClick();
                  }}
                  disabled={extractionLoading}
                  sx={{
                    ...harmonateUploadOutlinedSx,
                    fontSize: '0.7rem',
                    py: 0.5,
                    px: 1.5,
                    minWidth: '100px',
                    minHeight: 32,
                    bgcolor: 'transparent',
                    color: ht.white,
                    borderColor: 'rgba(255,255,255,0.7)',
                    '&:hover': {
                      borderColor: ht.white,
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                    '&.Mui-disabled': {
                      borderColor: 'rgba(255,255,255,0.35)',
                      color: 'rgba(255,255,255,0.5)',
                    },
                  }}
                >
                  {extractionLoading ? <CircularProgress size={16} sx={{ color: ht.white }} /> : 'Snowflake Info'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (currentFileForDetails) {
                      handleViewRawJson(currentFileForDetails);
                    }
                  }}
                  disabled={!currentFileForDetails}
                  sx={{
                    ...harmonateUploadOutlinedSx,
                    fontSize: '0.7rem',
                    py: 0.5,
                    px: 1.5,
                    minHeight: 32,
                    bgcolor: 'transparent',
                    color: ht.white,
                    borderColor: 'rgba(255,255,255,0.7)',
                    '&:hover': {
                      borderColor: ht.white,
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                    '&.Mui-disabled': {
                      borderColor: 'rgba(255,255,255,0.35)',
                      color: 'rgba(255,255,255,0.5)',
                    },
                  }}
                >
                  Raw JSON
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('Parser Status clicked');
                  }}
                  sx={{
                    ...harmonateUploadOutlinedSx,
                    fontSize: '0.7rem',
                    py: 0.5,
                    px: 1.5,
                    minHeight: 32,
                    bgcolor: 'transparent',
                    color: ht.white,
                    borderColor: 'rgba(255,255,255,0.7)',
                    '&:hover': {
                      borderColor: ht.white,
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  Parser Status
                </Button>
                {currentFileProcessStep === 'Failed' && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<ReplayIcon />}
                    onClick={handleRedriveClick}
                    disabled={redriveLoading || !documentDetails?.unitOfWorkId}
                    sx={{
                      ...harmonateProceedButtonSx,
                      minHeight: 32,
                      fontSize: '0.75rem',
                      bgcolor: ht.blue,
                      '&:hover': {
                        bgcolor: ht.blue,
                        opacity: 0.85,
                      },
                    }}
                  >
                    {redriveLoading ? 'Processing...' : 'Redrive'}
                  </Button>
                )}
                <IconButton
                  size="small"
                  onClick={() => {
                    setIsLogsModalOpen(false);
                    setCurrentFileProcessStep(null);
                  }}
                  sx={{ color: ht.white }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {logsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CircularProgress size={24} sx={{ color: ht.green }} />
              </Box>
            ) : (
              <Box sx={{ p: 2, overflow: 'auto', flex: 1, bgcolor: ht.listBg }}>
                {/* Document Details Section */}
                {documentDetails && (
                  <Box sx={{
                    mb: 3,
                    p: 2,
                    bgcolor: ht.white,
                    borderRadius: ht.cardRadius,
                    border: `1px solid ${ht.line}`,
                    boxShadow: ht.cardShadow,
                  }}>
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      mb: 1.5,
                      pb: 1,
                      borderBottom: `1px solid ${ht.line}`,
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ht.text }}>
                        DOCUMENT INFORMATION
                      </Typography>
                    </Box>
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                      gap: 2,
                      '& > div': {
                        minWidth: 0, // Prevents overflow
                      },
                      mb: 2
                    }}>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Document Name</Typography>
                        <Typography variant="body2" noWrap sx={{ color: ht.text, fontWeight: 500 }}>{documentDetails.name || 'N/A'}</Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Document Type</Typography>
                        <Typography variant="body2" sx={{ color: ht.text }}>{documentDetails.documentTypeId || 'N/A'}</Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Uploaded At</Typography>
                        <Typography variant="body2" sx={{ color: ht.text }}>
                          {documentDetails.uploadAt ? new Date(documentDetails.uploadAt).toLocaleString() : 'N/A'}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Location</Typography>
                        <Tooltip
                          title={documentDetails.location || 'N/A'}
                          arrow
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              maxWidth: '300px',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              display: 'inline-block',
                              verticalAlign: 'bottom',
                              fontSize: '0.875rem',
                              color: ht.text,
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                                textDecorationColor: ht.text,
                                textDecorationThickness: '1px',
                                textUnderlineOffset: '2px',
                              }
                            }}
                          >
                            {documentDetails.location || 'N/A'}
                          </Typography>
                        </Tooltip>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Unit of Work</Typography>
                        <Typography variant="body2" sx={{ color: ht.text }}>
                          {documentDetails.unitOfWorkId || 'N/A'}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Integration ID</Typography>
                        <Typography variant="body2" noWrap sx={{ color: ht.text }}>{documentDetails.integrationId || 'N/A'}</Typography>
                      </div>
                    </Box>
                  </Box>
                )}

                {/* Audit Logs Section */}
                <Box
                  sx={{
                    p: 2,
                    bgcolor: ht.white,
                    borderRadius: ht.cardRadius,
                    border: `1px solid ${ht.line}`,
                    boxShadow: ht.cardShadow,
                  }}
                >
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 1.5,
                    pb: 1,
                    borderBottom: `1px solid ${ht.line}`,
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ht.text }}>
                      AUDIT LOGS
                    </Typography>
                  </Box>
                  {auditLogs.length > 0 ? (
                    <Box sx={{ height: 'calc(100% - 40px)', width: '100%' }}>
                      <DataGridPro
                        rows={auditLogs}
                        pagination
                        // rowsPerPageOptions={[10, 25, 50]}
                        pageSize={10}
                        autoPageSize={false}
                        columns={[
                          {
                            field: 'createdAt',
                            headerName: 'Timestamp',
                            flex: 1,
                            minWidth: 160,
                            resizable: true,
                            valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : 'N/A',
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Timestamp
                              </Typography>
                            )
                          },
                          {
                            field: 'service',
                            headerName: 'Service',
                            flex: 0.8,
                            minWidth: 150,
                            resizable: true,
                            cellClassName: 'service-cell',
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Service
                              </Typography>
                            ),
                            renderCell: (params) => (
                              <Box sx={{
                                bgcolor: params.value === 'upload' ? ht.secondaryTab :
                                  params.value === 'document_classification' ? 'rgba(2, 107, 29, 0.12)' :
                                    params.value === 'extraction' ? 'rgba(46, 166, 160, 0.18)' :
                                      params.value === 'ingest' ? 'rgba(1, 48, 98, 0.12)' : ht.listBg,
                                color: params.value === 'upload' ? ht.text :
                                  params.value === 'document_classification' ? ht.green :
                                    params.value === 'extraction' ? ht.blue :
                                      params.value === 'ingest' ? ht.text : ht.text,
                                px: 1.5,  // Increased horizontal padding
                                py: 0.5, // Increased vertical padding
                                borderRadius: ht.buttonRadius,
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                minWidth: 'fit-content',
                                width: 'auto',
                                whiteSpace: 'nowrap',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {params.value || 'N/A'}
                              </Box>
                            )
                          },
                          {
                            field: 'action',
                            headerName: 'Action',
                            flex: 1,
                            minWidth: 120,
                            resizable: true,
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Action
                              </Typography>
                            ),
                            valueFormatter: (params) => params.value || 'N/A',
                            renderCell: (params) => (
                              <Box sx={{
                                color: ht.text,
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                fontFamily: 'monospace'
                              }}>
                                {params.formattedValue}
                              </Box>
                            )
                          },
                          {
                            field: 'status',
                            headerName: 'Status',
                            flex: 0.8,
                            minWidth: 100,
                            resizable: true,
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Status
                              </Typography>
                            ),
                            renderCell: (params) => (
                              <Box sx={{
                                color: ht.text,
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                textTransform: 'uppercase'
                              }}>
                                {params.value || 'N/A'}
                              </Box>
                            )
                          },
                          {
                            field: 'details',
                            headerName: 'Details',
                            flex: 2,
                            minWidth: 200,
                            resizable: true,
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Details
                              </Typography>
                            ),
                            renderCell: (params) => (
                              <Tooltip
                                title={params.value ?
                                  (typeof params.value === 'object' ?
                                    JSON.stringify(params.value, null, 2) :
                                    params.value.toString()) :
                                  'N/A'}
                                arrow
                                placement="top"
                              >
                                <Box sx={{
                                  width: '100%',
                                  '&:hover': {
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    textDecorationColor: ht.text,
                                    textDecorationThickness: '1px',
                                    textUnderlineOffset: '2px',
                                  }
                                }}>
                                  <Typography
                                    variant="body2"
                                    noWrap
                                    sx={{
                                      color: ht.text,
                                      fontSize: '0.8rem',
                                      fontFamily: 'monospace',
                                    }}
                                  >
                                    {params.value ?
                                      (typeof params.value === 'object' ?
                                        JSON.stringify(params.value) :
                                        params.value.toString()
                                      ).substring(0, 50) + (params.value.length > 50 ? '...' : '') :
                                      'N/A'}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )
                          }
                        ]}
                        rowsPerPageOptions={[10, 25, 50]}
                        disableSelectionOnClick
                        disableColumnMenu
                        hideFooterSelectedRowCount
                        sx={{
                          ...harmonateDataGridSx,
                          border: 'none',
                          '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: ht.listBg,
                            borderBottom: `1px solid ${ht.line}`,
                            minHeight: '40px !important',
                            maxHeight: '40px !important',
                          },
                          '& .MuiDataGrid-columnHeader': {
                            py: 1,
                          },
                          '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 600,
                            color: ht.text,
                          },
                          '& .MuiDataGrid-cell': {
                            fontSize: '0.8125rem',
                            color: ht.text,
                            py: 1,
                            borderColor: ht.line,
                          },
                          '& .MuiDataGrid-row': {
                            '&:hover': {
                              backgroundColor: ht.secondaryTab,
                            },
                            '&.Mui-selected': {
                              backgroundColor: ht.secondaryTab,
                              '&:hover': {
                                backgroundColor: ht.secondaryTab,
                              },
                            },
                          },
                          '& .MuiDataGrid-footerContainer': {
                            borderTop: `1px solid ${ht.line}`,
                            minHeight: '48px',
                          },
                        }}
                        components={{
                          LoadingOverlay: LinearProgress,
                          NoRowsOverlay: () => (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                              <Typography variant="body2" sx={{ color: ht.text }}>
                                No audit logs available
                              </Typography>
                            </Box>
                          ),
                        }}
                      />
                    </Box>
                  ) : (
                    <Typography sx={{ color: ht.text }}>No audit logs available</Typography>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      )}
{/* Add this Password Confirmation Dialog */}
<Dialog
  open={passwordDialogOpen}
  onClose={() => !isVerifying && setPasswordDialogOpen(false)}
  aria-labelledby="password-dialog-title"
  PaperProps={harmonateDialogPaperProps}
>
  <DialogTitle sx={harmonateDialogTitleSx} id="password-dialog-title">Confirm Your Identity</DialogTitle>
  <DialogContent sx={{ ...harmonateDialogContentSx, ...harmonateFormFieldsSx }}>
    <Typography variant="body1" sx={{ mb: 2 }}>
      Please enter your password to confirm deletion of {rowsToDelete.length} file(s).
    </Typography>
    <TextField
      autoFocus
      margin="dense"
      label="Password"
      type="password"
      autoComplete="off"
      fullWidth
      variant="outlined"
      value={password}
      onChange={(e) => {
        setPassword(e.target.value);
        setPasswordError('');
      }}
      error={!!passwordError}
      helperText={passwordError}
      disabled={isVerifying}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handlePasswordConfirm();
        }
      }}
    />
  </DialogContent>
  <DialogActions>
    <Button 
      onClick={() => {
        setPasswordDialogOpen(false);
        setPassword('');
        setPasswordError('');
      }} 
      disabled={isVerifying}
      variant="outlined"
      sx={harmonateCancelButtonSx}
    >
      Cancel
    </Button>
    <Button 
      onClick={handlePasswordConfirm} 
      variant="contained"
      disabled={isVerifying || !password.trim()}
      startIcon={isVerifying ? <CircularProgress size={20} color="inherit" /> : null}
      sx={harmonateAbortButtonSx}
    >
      {isVerifying ? 'Verifying...' : 'Confirm Deletion'}
    </Button>
  </DialogActions>
</Dialog>
      {/* Delete Confirmation Dialog */}
     <Dialog
  open={deleteAllConfirmOpen}
  onClose={() => !isBulkDeleting && setDeleteAllConfirmOpen(false)}
  aria-labelledby="delete-all-dialog-title"
  PaperProps={harmonateDialogPaperProps}
>
  <DialogTitle sx={harmonateDialogTitleSx} id="delete-all-dialog-title">Confirm Delete</DialogTitle>
  <DialogContent sx={harmonateDialogContentSx}>
    <Typography>
      Are you sure you want to delete {rowsToDelete.length} selected file(s)? 
      This action cannot be undone. You will be asked to confirm your password next.
    </Typography>
  </DialogContent>
  <DialogActions>
    <Button
      onClick={() => setDeleteAllConfirmOpen(false)}
      disabled={isBulkDeleting}
      variant="outlined"
      sx={harmonateCancelButtonSx}
    >
      Cancel
    </Button>
    <Button
      onClick={handleBulkDelete}
      variant="contained"
      disabled={isBulkDeleting}
      startIcon={isBulkDeleting ? <CircularProgress size={20} color="inherit" /> : null}
      sx={harmonateAbortButtonSx}
    >
      {isBulkDeleting ? 'Processing...' : 'Delete'}
    </Button>
  </DialogActions>
</Dialog>
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx} id="delete-dialog-title">
          Confirm File Deletion
        </DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>
            Are you sure you want to delete this file? This action cannot be undone.
          </Typography>
          {fileToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2"><strong>File Name:</strong> {fileToDelete.name}</Typography>
              <Typography variant="body2"><strong>File ID:</strong> {fileToDelete.id}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={harmonateAbortButtonSx}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reprocessConfirmOpen}
        onClose={() => {
          if (!isReprocessing) {
            setReprocessConfirmOpen(false);
            setFileToReprocess(null);
          }
        }}
        aria-labelledby="reprocess-dialog-title"
        aria-describedby="reprocess-dialog-description"
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx} id="reprocess-dialog-title">Confirm reprocess</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography id="reprocess-dialog-description">
            Are you sure you want to reprocess this file?
          </Typography>
          {fileToReprocess && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2"><strong>File name:</strong> {fileToReprocess.name}</Typography>
              <Typography variant="body2"><strong>Document ID:</strong> {fileToReprocess.reprocessDocumentId}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setReprocessConfirmOpen(false);
              setFileToReprocess(null);
            }}
            disabled={isReprocessing}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReprocessConfirm}
            variant="contained"
            disabled={isReprocessing}
            startIcon={isReprocessing ? <CircularProgress size={20} color="inherit" /> : null}
            sx={harmonateProceedButtonSx}
          >
            {isReprocessing ? 'Processing…' : 'Reprocess'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Redrive Confirmation Dialog */}
      <Dialog
        open={redriveConfirmOpen}
        onClose={() => setRedriveConfirmOpen(false)}
        aria-labelledby="redrive-dialog-title"
        aria-describedby="redrive-dialog-description"
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx} id="redrive-dialog-title">
          Confirm Workflow Redrive
        </DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>
            Are you sure you want to redrive the workflow for this document? This will restart the processing pipeline.
          </Typography>
          {documentDetails && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2"><strong>Document:</strong> {documentDetails.name}</Typography>
              <Typography variant="body2"><strong>Unit of Work:</strong> {documentDetails.unitOfWorkId}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRedriveConfirmOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleRedriveConfirm}
            variant="contained"
            disabled={redriveLoading}
            sx={harmonateProceedButtonSx}
          >
            {redriveLoading ? 'Processing...' : 'Confirm Redrive'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Redrive Status Dialog */}
      <Dialog
        open={redriveStatusDialog.open}
        onClose={handleCloseStatusDialog}
        aria-labelledby="status-dialog-title"
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx} id="status-dialog-title">
          Redrive Status
        </DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2">
              <strong>Execution ARN:</strong> {redriveStatusDialog.executionArn}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                <strong>Status:</strong>
              </Typography>
              <Box sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                bgcolor: redriveStatusDialog.status === 'SUCCEEDED' ? 'success.light' :
                  redriveStatusDialog.status === 'FAILED' ? 'error.light' :
                    redriveStatusDialog.status === 'RUNNING' ? 'warning.light' : 'grey.200',
                color: redriveStatusDialog.status === 'SUCCEEDED' ? 'success.dark' :
                  redriveStatusDialog.status === 'FAILED' ? 'error.dark' :
                    redriveStatusDialog.status === 'RUNNING' ? 'warning.dark' : 'text.secondary',
                fontSize: '0.875rem',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                {redriveStatusDialog.status === 'RUNNING' && (
                  <CircularProgress size={16} color="inherit" />
                )}
                {redriveStatusDialog.status}
              </Box>
            </Box>
            {redriveStatusDialog.status === 'RUNNING' && (
              <Typography variant="caption" color="text.secondary">
                Status will update automatically. This dialog will remain open until completion.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseStatusDialog}
            color="primary"
            disabled={redriveStatusDialog.status === 'RUNNING'}
          >
            {redriveStatusDialog.status === 'RUNNING' ? 'Running...' : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 3-dot Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MuiMenuItem onClick={handleMoveToUnidentified}>
          Move to Unidentified
        </MuiMenuItem>
      </Menu>

      {/* Common Toast Component */}
      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
        variant={toastState.variant}
      />

      {/* Extraction Details Dialog */}
      <Dialog
        open={extractionDialogOpen}
        onClose={() => setExtractionDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'inherit' }}>Extraction Details</Typography>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeExtractionTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                minHeight: '48px',
              },
              '& .Mui-selected': {
                color: theme => theme.palette.primary.main,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: theme => theme.palette.primary.main,
                height: '3px',
              },
            }}
          >
            <Tab label="Extracted Data" />
            <Tab label="Base" />
            <Tab label="Landing" />
          </Tabs>
        </Box>

        <DialogContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
          {activeExtractionTab === 0 && (
            <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
              {extractionLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : extractionDetails ? (
                <pre style={{
                  backgroundColor: ht.listBg,
                  padding: '16px',
                  borderRadius: '4px',
                  maxHeight: '60vh',
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  margin: 0
                }}>
                  {JSON.stringify(extractionDetails, null, 2)}
                </pre>
              ) : (
                <Box p={3} textAlign="center">
                  <Typography color="textSecondary">No extraction data available</Typography>
                </Box>
              )}
            </Box>
          )}

          {activeExtractionTab === 1 && (
            <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
              {baseInternalCasLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : baseInternalCasError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <AlertTitle>Error Loading Base Internal CAS</AlertTitle>
                  {baseInternalCasError}
                </Alert>
              ) : baseInternalCasData ? (
                <pre style={{
                  backgroundColor: ht.listBg,
                  padding: '16px',
                  borderRadius: '4px',
                  maxHeight: '60vh',
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  margin: 0
                }}>
                  {JSON.stringify(baseInternalCasData, null, 2)}
                </pre>
              ) : (
                <Box p={3} textAlign="center">
                  <Typography color="textSecondary">No Base Internal CAS data available</Typography>
                </Box>
              )}
            </Box>
          )}

          {activeExtractionTab === 2 && (
            <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
              {landingInternalCasLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : landingInternalCasError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <AlertTitle>Error Loading Landing Internal CAS</AlertTitle>
                  {landingInternalCasError}
                </Alert>
              ) : landingInternalCasData ? (
                <pre style={{
                  backgroundColor: ht.listBg,
                  padding: '16px',
                  borderRadius: '4px',
                  maxHeight: '60vh',
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  margin: 0
                }}>
                  {JSON.stringify(landingInternalCasData, null, 2)}
                </pre>
              ) : (
                <Box p={3} textAlign="center">
                  <Typography color="textSecondary">No Landing Internal CAS data available</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <Button
            onClick={() => setExtractionDialogOpen(false)}
            color="primary"
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Content Dialog */}
      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>File Content</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          {jsonLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : jsonError ? (
            <Alert severity="error">
              <AlertTitle>Error</AlertTitle>
              {jsonError}
            </Alert>
          ) : jsonData && jsonData.isBinary ? (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Binary File Detected
              </Typography>
              <Typography variant="body1" paragraph>
                This is a binary file and cannot be displayed directly.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                component="a"
                href={jsonData.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ mt: 2 }}
              >
                Download File
              </Button>
            </Box>
          ) : (
            <pre style={{
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              fontFamily: 'monospace',
              maxHeight: '60vh',
              overflow: 'auto',
              padding: '16px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px'
            }}>
              {jsonData?.content || 'No data available'}
            </pre>
          )}
        </DialogContent>
        <DialogActions>
          {jsonData?.downloadUrl && (
            <Button
              component="a"
              href={jsonData.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
            >
              Download
            </Button>
          )}
          <Button onClick={() => setJsonDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WorkflowDrawer;